import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  Component,
  ElementRef,
  EventEmitter,
  Host,
  Inject,
  Input,
  OnChanges,
  Optional,
  Output,
  SimpleChanges,
  SkipSelf,
  ViewChild,
  forwardRef,
} from '@angular/core';
import { ControlContainer, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatChipGrid } from '@angular/material/chips';
import { CommonUtilityLib, StorageUtilityLib, USER_SESSION_TOKEN, UserSession } from '@thehubz/client/core';
import { User } from '@thehubz/client/data-access-api';
import { AnyType } from '@thehubz/shared/model';
import { MatChipInputEvent } from '../../mat-chip-patched/chip-input';

@Component({
  selector: 'sp-user-email-input-autocomplete',
  templateUrl: './user-email-input-autocomplete.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => UserEmailInputAutocompleteComponent),
    },
  ],
})
export class UserEmailInputAutocompleteComponent implements ControlValueAccessor, OnChanges {
  /** user data */
  @Input() users!: User[];
  /** configuration to enable email chips */
  @Input() enableChips = false;
  /** if form field is required */
  @Input() required?: boolean;
  /** formControlName string value */
  @Input() formControlName!: string;
  /** form field is required text */
  @Input() requiredText = '';
  /** emit a single user */
  @Output() selectedUser = new EventEmitter<User>();
  /** emit a string of user array */
  @Output() selectedUserArray = new EventEmitter<string[]>();
  /** chip keycodes handlers */
  separatorKeysCodes = [ENTER, COMMA];

  @ViewChild('emailInput') emailInput!: ElementRef<HTMLInputElement>;
  @ViewChild('chipInput') chipInput!: ElementRef<HTMLInputElement>;
  @ViewChild('chipList') chipList!: MatChipGrid;

  change!: (value: string | null) => void;
  onTouched!: () => void;

  isDisabled!: boolean;
  emailList: string[] = [];
  removable = true;
  value = '';

  constructor(
    @Inject(USER_SESSION_TOKEN) private userSessionToken: string,
    @Optional() @Host() @SkipSelf() private controlContainer: ControlContainer,
    private storageUtility: StorageUtilityLib,
    private commonUtilityLib: CommonUtilityLib,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['required'] && this.chipList) {
      this.chipList.errorState = !!changes['required'].currentValue;
      this.controlContainer?.control?.get(this.formControlName)?.setErrors({ required: changes['required'].currentValue });
    }
  }

  selectionOptions(user: User) {
    if (this.checkDuplicateEmail(user.email)) this.emailList.push(user.email);
    if (this.emailInput && this.emailInput.nativeElement) this.emailInput.nativeElement.value = user.email;
    this.change(user.email);
    if (this.enableChips) {
      this.chipInput.nativeElement.value = '';
      this.selectedUserArray.emit(this.emailList);
      if (this.emailList.length) this.chipList.errorState = false;
    } else {
      this.selectedUser.emit(user);
    }
    this.users = [];
  }

  addLoggedInUserEmail() {
    if (this.enableChips) {
      const userSession = this.storageUtility.getLocalStorage<UserSession>(this.userSessionToken);
      if (this.checkDuplicateEmail(userSession.userData.email)) {
        this.emailList.push(userSession.userData.email);
        this.change(userSession.userData.email);
        this.selectedUserArray.emit(this.emailList);
        if (this.emailList.length) this.chipList.errorState = false;
      }
    }
  }

  onChange(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    if (value) {
      this.change(value);
      this.onTouched();
    } else {
      this.users = [];
    }
  }

  writeValue(value: string): void {
    if (value && typeof value !== 'object') this.value = value;
  }
  registerOnChange(fn: AnyType) {
    this.change = fn;
  }
  registerOnTouched(fn: AnyType): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }
  /**
   * Add chip
   * @param event
   */
  add(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (this.isEmailValid(value)) {
      // Split the input value into an array of email addresses
      const splitValue: string[] = value.split(/[\s,;]+/);
      splitValue.forEach((val) => {
        if (this.checkDuplicateEmail(val)) {
          this.emailList.push(val);
        }
      });
      this.selectedUserArray.emit(this.emailList);
      if (this.emailList.length) this.chipList.errorState = false;
    } else if (!this.emailList.length) {
      this.chipList.errorState = true;
      this.controlContainer.control?.get(this.formControlName)?.patchValue('');
      this.controlContainer.control?.get(this.formControlName)?.setErrors({ required: true });
      this.controlContainer.control?.get(this.formControlName)?.updateValueAndValidity();
    }
    // Clear the input value
    event.chipInput?.clear();
  }
  /**
   * Remove chip
   * @param data
   */
  removeEmail(data: string): void {
    if (this.emailList.indexOf(data) >= 0) {
      this.emailList.splice(this.emailList.indexOf(data), 1);
    }
    if (this.enableChips) {
      if (this.emailList.length === 0) this.change('');
      this.selectedUserArray.emit(this.emailList);
    }
  }

  private checkDuplicateEmail(value: string): boolean {
    return !this.emailList.some((email) => email === value);
  }

  /**
   * Check if the provided string is a valid email address or a list of email addresses separated by white spaces, commas, or semi-colons
   * @param value
   * @returns
   */
  private isEmailValid(value: string): boolean {
    // Check if the string contains any white spaces, commas, or semi-colons; if so, it is considered a list of email addresses
    return value.search(/[\s,;]+/) !== -1 || this.commonUtilityLib.validateEmail(value);
  }
}
